<template>
    <div>
        <p class="text-theme-muted text-sm mb-4">
            Para simular la entrega proporciona la ciudad de destino, la
            cantidad de unidades y la fecha del pedido. El sistema calculará los
            tiempos de preparación y las fechas de entrega disponibles.
        </p>

        <div class="flex flex-wrap gap-y-4">
            <div class="w-full md:w-1/2 px-2">
                <label class="block mb-1 text-sm text-theme-muted">
                    Fecha de pedido
                </label>
                <date-hour-picker
                    v-model="simulateInput.date"
                    locale="es"
                    size="sm"
                    class="mt-1"
                />
            </div>
            <div class="w-full md:w-1/2 px-2">
                <custom-input
                    v-model="simulateInput.referenceQuantity"
                    label="Unidades"
                    type="number"
                    min="1"
                />
            </div>
            <div class="w-full md:w-1/2 px-2">
                <city-code-selector
                    v-model="simulateInput.cityCode"
                    label="Ciudad de entrega"
                />
            </div>
            <div class="w-full md:w-1/2 px-2">
                <template v-if="initialPriceOffer > 0">
                    <label class="block mb-1 text-sm text-theme-muted">
                        Precio de referencia
                    </label>
                    <p class="text-theme-base font-semibold text-lg mt-1">
                        {{ simulateInput.referencePriceOffer | currency }}
                    </p>
                </template>
                <input-money-component
                    v-else
                    v-model="simulateInput.referencePriceOffer"
                    label="Precio de referencia"
                    icon="💲"
                />
            </div>
            <div class="w-full px-2">
                <custom-checkbox v-model="simulateInput.alwaysStock" primary>
                    Simular con inventario siempre disponible
                </custom-checkbox>
            </div>
            <div class="w-full flex justify-center px-2">
                <app-button
                    size="large"
                    :loading="isLoadingSimulate"
                    @click="runSimulation"
                >
                    Simular 🔬
                </app-button>
            </div>
        </div>

        <app-dialog v-model="showSimulation" size="lg">
            <template #header>
                <span class="text-lg font-bold text-primary">
                    Resultado de la simulación
                </span>
            </template>

            <div class="w-full flex flex-wrap gap-4">
                <div
                    class="w-full border border-brand-blue rounded-lg p-4 flex items-center justify-between gap-4"
                >
                    <div>
                        <p class="text-theme-muted text-sm mb-1">
                            Precio de entrega
                        </p>
                        <span class="text-xl font-semibold text-theme-primary">
                            {{ simulationResult.deliveryPrice | currency }}
                        </span>
                    </div>
                    <app-button
                        flat
                        size="small"
                        @click="showExplanation = !showExplanation"
                    >
                        {{
                            showExplanation
                                ? "Ocultar explicación"
                                : "Explicación"
                        }}
                    </app-button>
                </div>

                <div v-if="showExplanation" class="w-full space-y-1">
                    <p class="text-sm text-theme-muted">
                        Costo base de envío:
                        <span class="text-theme-base font-medium">
                            {{ simulationResult.deliveryCostBase | currency }}
                        </span>
                    </p>
                    <p class="text-sm text-theme-muted">
                        Costo adicional de envío:
                        <span class="text-theme-base font-medium">
                            {{
                                simulationResult.additionalDeliveryCost
                                    | currency
                            }}
                        </span>
                    </p>
                    <p class="text-sm text-theme-muted">
                        Horas adicionales de entrega:
                        <span class="text-theme-base font-medium">
                            {{ additionalDeliveryHoursLabel }}
                        </span>
                    </p>
                    <p class="text-sm text-theme-muted">
                        Tiempo de preparación aplicado:
                        <span class="text-theme-base font-medium">
                            {{ prepareTimeLabel }}
                        </span>
                    </p>
                    <p class="text-sm text-theme-muted">
                        Hora de corte de entrega:
                        <span class="text-theme-base font-medium">
                            {{ simulationResult.thresholdHour }}
                        </span>
                    </p>
                    <p class="text-sm text-theme-muted">
                        Hora de corte de empaque:
                        <span class="text-theme-base font-medium">
                            {{ simulationResult.packageThresholdHour }}
                        </span>
                    </p>
                    <p class="text-sm text-theme-muted">
                        Anticipación empaque:
                        <span class="text-theme-base font-medium">
                            {{ simulationResult.offsetPackageHours }} horas
                        </span>
                    </p>
                    <p class="text-sm text-theme-muted">
                        Anticipación entrega:
                        <span class="text-theme-base font-medium">
                            {{ simulationResult.offsetDeliveryHours }} horas
                        </span>
                    </p>
                    <p class="text-sm text-theme-muted">
                        Días de entrega:
                        <span class="text-theme-base font-medium">
                            {{ deliveryWeekdaysLabel }}
                        </span>
                    </p>
                    <p class="text-sm text-theme-muted">
                        Configuración logística aplicada:
                        <span class="text-theme-base font-medium">
                            {{ simulationResult.logisticConfigurationName }}
                        </span>
                    </p>
                    <p class="text-sm text-theme-muted">
                        {{ configurationModeLabel }}
                    </p>
                    <p
                        v-if="simulationResult.unknownCity"
                        class="text-sm text-theme-warning"
                    >
                        La ciudad de entrega no está registrada en los precios
                        por ciudad, se aplicó el precio para ciudades
                        desconocidas.
                    </p>
                </div>

                <div class="w-full border border-brand-blue rounded-lg p-4">
                    <p class="text-theme-muted text-sm mb-2">
                        Fecha en que está listo el producto para despacho
                    </p>
                    <date-delivery-viewer
                        :input="simulationResult.packageDate"
                    />
                </div>

                <div class="w-full border border-brand-blue rounded-lg p-4">
                    <p class="text-theme-muted text-sm mb-2">
                        Fechas de entrega disponibles
                    </p>
                    <div class="flex flex-wrap gap-2">
                        <date-delivery-viewer
                            v-for="(item, i) in simulationResult.deliveryDates"
                            :key="i"
                            :input="item"
                            hide-hour
                        />
                    </div>
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import { DateTime } from "luxon";
import { mapActions, mapGetters } from "vuex";

import { weekdayItems } from "@/common/fields/weekdays";

import AppDialog from "@/components/utils/AppDialog.vue";
import CityCodeSelector from "@/components/utils/CityCodeSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import DateDeliveryViewer from "@/components/utils/DateDeliveryViewer.vue";
import DateHourPicker from "@/components/utils/DateHourPicker.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";

export default {
    name: "DeliverySimulator",
    components: {
        AppDialog,
        DateHourPicker,
        CityCodeSelector,
        DateDeliveryViewer,
        InputMoneyComponent,
        CustomInput
    },
    props: {
        logisticConfigurationSlug: {
            type: String,
            default: "default"
        },
        initialPriceOffer: {
            type: Number,
            default: 0
        },
        initialAlwaysStock: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isLoadingSimulate: false,
            showSimulation: false,
            showExplanation: false,
            simulateInput: {
                cityCode: "co",
                date: DateTime.now().toUTC().toISO(),
                referenceQuantity: 1,
                referencePriceOffer: this.initialPriceOffer,
                alwaysStock: this.initialAlwaysStock
            },
            simulationResult: {
                deliveryPrice: 0,
                packageDate: { date: "", hour: "", format: "" },
                deliveryDates: [],
                deliveryCostBase: 0,
                additionalDeliveryCost: 0,
                additionalDeliveryHours: 0,
                prepareTime: { minutes: 0, hours: 0, days: 0, months: 0 },
                logisticConfigurationName: "",
                logisticConfigurationSlug: "",
                configurationMode: "",
                unknownCity: false,
                thresholdHour: "",
                packageThresholdHour: "",
                deliveryWeekdays: [],
                offsetPackageHours: 0,
                offsetDeliveryHours: 0
            }
        };
    },
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        prepareTimeLabel() {
            const { days, hours, minutes, months } =
                this.simulationResult.prepareTime;
            const parts = [
                months && `${months} meses`,
                days && `${days} días`,
                hours && `${hours} horas`,
                minutes && `${minutes} minutos`
            ].filter(Boolean);

            return parts.length ? parts.join(", ") : "0 horas";
        },
        additionalDeliveryHoursLabel() {
            const totalHours = this.simulationResult.additionalDeliveryHours;
            const days = Math.floor(totalHours / 24);
            const hours = totalHours % 24;
            const parts = [
                days && `${days} días`,
                hours && `${hours} horas`
            ].filter(Boolean);

            return parts.length ? parts.join(", ") : "0 horas";
        },
        deliveryWeekdaysLabel() {
            const weekdays = this.simulationResult.deliveryWeekdays || [];
            return weekdayItems
                .filter((item) => weekdays.includes(item.value))
                .map((item) => item.label)
                .join(", ");
        },
        configurationModeLabel() {
            const suffix =
                this.simulationResult.configurationMode === "provided"
                    ? "(específica)"
                    : "(por defecto del tenant)";

            return `Configuración usada: ${this.simulationResult.logisticConfigurationName} ${suffix}`;
        }
    },
    watch: {
        initialPriceOffer(value) {
            this.simulateInput.referencePriceOffer = value;
        },
        initialAlwaysStock(value) {
            this.simulateInput.alwaysStock = value;
        }
    },
    methods: {
        ...mapActions("logisticConfigurations", [
            "simulateLogisticConfiguration"
        ]),
        async runSimulation() {
            this.isLoadingSimulate = true;
            this.showSimulation = false;
            try {
                this.simulationResult =
                    await this.simulateLogisticConfiguration({
                        slug: this.logisticConfigurationSlug,
                        ...this.simulateInput
                    });
                this.showSimulation = true;
                this.isLoadingSimulate = false;
            } catch (error) {
                this.isLoadingSimulate = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
